import React from "react";
import PropTypes from "prop-types";
import { useStaticQuery, graphql } from "gatsby";

import { getMediaUrl } from "./media";

// Normalize the ACF `social_links_repeater` rows (each row: { link, icon })
// into a clean list, dropping any row missing a URL or an icon.
const normalizeSocialLinks = (rows) => {
  if (!Array.isArray(rows)) return [];

  return rows
    .map((row) => ({
      link: row?.link || "",
      icon: getMediaUrl(row?.icon),
      alt: row?.icon?.altText || "",
    }))
    .filter((row) => row.link && row.icon);
};

// The social links live in the global `landing_settings` group, which the
// flexible-content sections (hero, simple hero) don't receive as props. Pull
// them straight from the landing page (aicustomblocks) the same way the layout
// fetches its chrome, so any landing section can render them without threading
// settings through the auto-generated page templates.
const useSocialLinksFromSettings = () => {
  const data = useStaticQuery(graphql`
    query LandingSocialLinksQuery {
      wpPage(slug: { eq: "aicustomblocks" }) {
        landing_flexible {
          landingSettings {
            socialLinksRepeater {
              link
              icon {
                altText
                mediaItemUrl
                sourceUrl
              }
            }
          }
        }
      }
    }
  `);

  return data?.wpPage?.landing_flexible?.landingSettings?.socialLinksRepeater;
};

// `baseClass` is the section block (e.g. "landing_hero_section") so the same
// markup yields the section-specific class names the CSS expects.
// `links` lets callers that already have the data (footer) pass it in directly;
// otherwise the links are fetched from the landing settings.
const LandingSocialLinks = ({ links, baseClass, className }) => {
  const fetched = useSocialLinksFromSettings();
  const socialLinks = normalizeSocialLinks(links || fetched);

  if (!socialLinks.length) return null;

  return (
    <div
      className={`${baseClass}__socials${className ? ` ${className}` : ""}`}
    >
      {socialLinks.map((social, index) => (
        <a
          className={`${baseClass}__social`}
          href={social.link}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={social.alt || "Social link"}
          key={`${social.link}-${index}`}
        >
          <img
            className={`${baseClass}__social_icon`}
            src={social.icon}
            alt=""
            loading="lazy"
          />
        </a>
      ))}
    </div>
  );
};

LandingSocialLinks.propTypes = {
  baseClass: PropTypes.string.isRequired,
  className: PropTypes.string,
  links: PropTypes.arrayOf(
    PropTypes.shape({
      link: PropTypes.string,
      icon: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    })
  ),
};

LandingSocialLinks.defaultProps = {
  className: undefined,
  links: undefined,
};

export default LandingSocialLinks;